import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

interface StatTileProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: string;
    isPositive: boolean;
  };
  onClick?: () => void;
}

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const StatTile: React.FC<StatTileProps> = ({
  title,
  value,
  subtitle,
  icon,
  color,
  trend,
  onClick,
}) => {
  return (
    <Box
      component={motion.div}
      variants={itemVariants}
      whileHover={onClick ? {
        scale: 1.03,
        transition: { duration: 0.2 },
      } : {
        scale: 1.02,
        transition: { duration: 0.2 },
      }}
      onClick={onClick}
      sx={{
        position: 'relative',
        borderRadius: '20px',
        background: 'rgba(26, 26, 26, 0.7)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        p: 3,
        cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          borderColor: `${color}40`,
          boxShadow: `0 8px 32px ${color}15`,
        },
      }}
    >
      {/* Background glow effect */}
      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Icon */}
        <Box
          sx={{
            display: 'inline-flex',
            p: 1.5,
            borderRadius: '14px',
            background: `${color}15`,
            mb: 2,
          }}
        >
          <Box sx={{ color: color, display: 'flex' }}>
            {icon}
          </Box>
        </Box>

        {/* Value */}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 0.5,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          {value}
        </Typography>

        {/* Title */}
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
            mb: trend ? 1.5 : 0,
          }}
        >
          {title}
        </Typography>

        {/* Trend indicator */}
        {trend && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {trend.isPositive ? (
              <TrendingUp sx={{ fontSize: 16, color: '#10b981' }} />
            ) : (
              <TrendingDown sx={{ fontSize: 16, color: '#ef4444' }} />
            )}
            <Typography
              variant="caption"
              sx={{
                color: trend.isPositive ? '#10b981' : '#ef4444',
                fontWeight: 500,
              }}
            >
              {trend.value}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', ml: 0.5 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StatTile;
